import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Container,
  CircularProgress
} from '@mui/material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useOptInStore } from './store/optInStore';
import { useAuthStore } from './store/authStore';
import LicenseAgreementText from './LicenseAgreementText';

const License = () => {
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loadingAgreement, setLoadingAgreement] = useState(true);
  const acceptLicense = useOptInStore(state => state.acceptLicense);
  const fetchLicenseStatus = useOptInStore(state => state.fetchLicenseStatus);
  const license = useOptInStore(state => state.license);
  const user = useAuthStore(state => state.user);
  const navigate = useNavigate();
  const userEmail = user?.email || user?.id || '';
  const agreement = license?.email === userEmail ? license?.agreement : null;
  
  // Check authentication on component mount
  useEffect(() => {
    // Short timeout to ensure auth state is loaded
    const timer = setTimeout(() => {
      setCheckingAuth(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  // Load the agreement that applies to this user (per-company or default).
  // Already accepted it (e.g. landed here from a stale cache)? Move on.
  useEffect(() => {
    if (!userEmail) return;
    setLoadingAgreement(true);
    fetchLicenseStatus(userEmail)
      .then((status) => {
        if (!status) {
          setError('Could not load the license agreement. Please reload the page.');
        } else if (status.accepted) {
          navigate(`/report-list/${user.id}`, { replace: true });
        }
      })
      .finally(() => setLoadingAgreement(false));
  }, [userEmail, fetchLicenseStatus, navigate, user?.id]);
  
  // If not authenticated, redirect to login page
  if (!checkingAuth && !user) {
    return <Navigate to="/" replace />;
  }
  
  const handleAccept = async () => {
    if (!accepted) {
      setError('You must accept the license agreement to continue.');
      return;
    }
    
    if (!user) {
      setError('You must be logged in to continue.');
      navigate('/');
      return;
    }

    if (!agreement?.id) {
      setError('The license agreement has not loaded yet. Please reload the page.');
      return;
    }
    
    setLoading(true);
    try {
      await acceptLicense(agreement.id);
      // Redirect to reports page after successful acceptance
      navigate(`/report-list/${user.id}`);
    } catch (error) {
      console.error('Error accepting license:', error);
      if (error?.response?.status === 409) {
        // The agreement that applies changed since it was shown — reload it.
        setAccepted(false);
        await fetchLicenseStatus(userEmail);
        setError('The license agreement has been updated. Please review it and accept again.');
      } else {
        setError('Failed to record your acceptance. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleDecline = () => {
    // Log out and redirect to login
    localStorage.removeItem('userEmail');
    sessionStorage.removeItem('userInfo');
    navigate('/');
    window.location.reload(); // Force reload to clear any auth state
  };

  if (checkingAuth || (loadingAgreement && !agreement)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#b3e5fc",
        backgroundImage: "url(/freeze_data.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        paddingTop: "50px",
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.95)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ color: '#2F3F5C' }}>
            License Agreement
          </Typography>
          {agreement?.version && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {agreement.title} — version {agreement.version}
            </Typography>
          )}
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <Paper
            elevation={0}
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              p: 2,
              backgroundColor: '#f8f8f8',
              border: '1px solid #ddd',
              mb: 3
            }}
          >
            <LicenseAgreementText agreement={agreement} />
          </Paper>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox 
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
              }
              label="I have read and agree to the license agreement"
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="outlined" 
                color="error"
                onClick={handleDecline}
              >
                Decline
              </Button>
              
              <Button 
                variant="contained"
                color="primary"
                disabled={!accepted || loading || !agreement}
                onClick={handleAccept}
              >
                {loading ? 'Processing...' : 'Accept'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </div>
  );
};

export default License;